import {
  Typography,
  Breadcrumbs,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Box,
  TextField,
  Button,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  LinearProgress,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import SmsIcon from "@mui/icons-material/Sms";
import PhoneIcon from "@mui/icons-material/Phone";
import { Link } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import axios, { CancelTokenSource } from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { useSelector } from "react-redux";
import { show as showSnackbar } from "../../redux/slices/snackbar";
import { useAppDispatch } from "../../hooks";
import { toThousandSeperatorNumber } from "../../helpers/format-helper";

interface MessageStatistics {
  period: {
    fromDate: string;
    toDate: string;
  };
  summary: {
    total: number;
    sent: number;
    failed: number;
    queued: number;
    sending: number;
    successRate: number;
    totalCost: number;
    avgCost: number | null;
  };
  dailyStatistics: Array<{
    _id: string;
    totalMessages: number;
    date: string;
    totalCost: number;
    sent: number;
    failed: number;
    queued: number;
    sending: number;
  }>;
}

interface PortStatistics {
  deviceId?: string;
  deviceName?: string;
  port: number | null;
  totalMessages: number;
  successCount: number;
  failedCount: number;
  successRate: number;
  lastSentAt?: string;
}

interface PortReportData {
  period: {
    fromDate: string;
    toDate: string;
  };
  summary: {
    totalDevices: number;
    totalPorts: number;
    totalMessages: number;
    totalSuccess: number;
    totalFailed: number;
    overallSuccessRate: number;
  };
  portStatistics: PortStatistics[];
}

// Gateway real-time data interfaces
interface GatewayPort {
  index: number;
  msisdn: string;
  operator: string;
  state: string;
  signal: number;
  dailySentCount: number;
  dailySuccessCount: number;
  dailySuccessRate: number;
  lastSentAt: number;
  isBlocked: boolean;
  blockReasons?: string[];
  nextSendTime?: number;
  minIntervalMinutes?: number;
  balance?: {
    amount: number;
    currency: string;
  };
}

interface Gateway {
  id: string;
  name: string;
  model: string;
  status: string;
  description: string;
  attributes: {
    ports: GatewayPort[];
    appVersion?: string;
    lastUpdated?: string;
    allowOutboundSMS?: boolean;
    allowOutboundCall?: boolean;
  };
}

interface GatewayResponse {
  data: Gateway[];
  paging: {
    index: number;
    size: number;
    totalPages: number;
    totalItems: number;
  };
}

export function LCM_ReportSMS() {
  const dispatch = useAppDispatch();
  const user = useSelector((state: any) => state.user);

  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [messageStats, setMessageStats] = useState<MessageStatistics | null>(
    null
  );
  const [portStats, setPortStats] = useState<PortReportData | null>(null);
  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingPortStats, setIsLoadingPortStats] = useState(false);
  const [isLoadingGateways, setIsLoadingGateways] = useState(false);

  // SMS Toggle Dialog State
  const [openSmsConfirmDialog, setOpenSmsConfirmDialog] = useState(false);
  const [selectedGatewayForSms, setSelectedGatewayForSms] =
    useState<Gateway | null>(null);
  const [isUpdatingSms, setIsUpdatingSms] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  // Cancel token refs to prevent concurrent requests
  const messageStatsCancel = useRef<CancelTokenSource | null>(null);
  const portStatsCancel = useRef<CancelTokenSource | null>(null);
  const gatewaysCancel = useRef<CancelTokenSource | null>(null);
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);

  // Set default dates: from start of month to today
  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    setFromDate(firstDayOfMonth.toISOString().split("T")[0]);
    setToDate(today.toISOString().split("T")[0]);
  }, []);

  const loadMessageStatistics = (isShowSnackbar = true) => {
    if (!fromDate || !toDate || isProcessing) return;

    // Cancel previous request if still pending
    if (messageStatsCancel.current) {
      messageStatsCancel.current.cancel("New request initiated");
    }

    // Create new cancel token
    messageStatsCancel.current = axios.CancelToken.source();

    const apiURL = `https://api.hydrabyte.co/msg/reports/message-statistics?fromDate=${fromDate}&toDate=${toDate}`;
    const axiosConfig = {
      method: "get",
      headers: {
        Authorization: `bearer ${user.accessToken}`,
        "Content-Type": "application/json",
      },
      url: apiURL,
      cancelToken: messageStatsCancel.current.token,
    };

    setIsProcessing(true);
    axios(axiosConfig)
      .then(function (response) {
        setMessageStats(response.data);
        if (isShowSnackbar) {
          dispatch(
            showSnackbar({
              title: "Thành công",
              content: "Tải dữ liệu báo cáo SMS thành công!",
              type: "success",
            })
          );
        }
      })
      .catch(function (error) {
        if (axios.isCancel(error)) {
          console.log("Request canceled:", error.message);
          return;
        }
        if (isShowSnackbar) {
          dispatch(
            showSnackbar({
              title: `Tải dữ liệu lỗi!`,
              content: `${error.message}`,
              type: "error",
            })
          );
        }
      })
      .then(function () {
        setIsProcessing(false);
        messageStatsCancel.current = null;
      });
  };

  const loadPortStatistics = (isShowSnackbar = true) => {
    if (!fromDate || !toDate || isLoadingPortStats) return;

    // Cancel previous request if still pending
    if (portStatsCancel.current) {
      portStatsCancel.current.cancel("New request initiated");
    }

    // Create new cancel token
    portStatsCancel.current = axios.CancelToken.source();

    const apiURL = `https://api.hydrabyte.co/msg/reports/port-statistics?fromDate=${fromDate}&toDate=${toDate}`;
    const axiosConfig = {
      method: "get",
      headers: {
        Authorization: `bearer ${user.accessToken}`,
        "Content-Type": "application/json",
      },
      url: apiURL,
      cancelToken: portStatsCancel.current.token,
    };

    setIsLoadingPortStats(true);
    axios(axiosConfig)
      .then(function (response) {
        setPortStats(response.data);
        if (isShowSnackbar) {
          dispatch(
            showSnackbar({
              title: "Thành công",
              content: "Tải dữ liệu báo cáo Port thành công!",
              type: "success",
            })
          );
        }
      })
      .catch(function (error) {
        if (axios.isCancel(error)) {
          console.log("Port stats request canceled:", error.message);
          return;
        }
        if (isShowSnackbar) {
          dispatch(
            showSnackbar({
              title: `Tải dữ liệu Port lỗi!`,
              content: `${error.message}`,
              type: "error",
            })
          );
        }
      })
      .then(function () {
        setIsLoadingPortStats(false);
        portStatsCancel.current = null;
      });
  };

  const loadGateways = (isShowSnackbar = false) => {
    if (isLoadingGateways) return;

    // Cancel previous request if still pending
    if (gatewaysCancel.current) {
      gatewaysCancel.current.cancel("New request initiated");
    }

    // Create new cancel token
    gatewaysCancel.current = axios.CancelToken.source();

    const apiURL = `https://api.hydrabyte.co/msg/gateways?pageSize=200`;
    const axiosConfig = {
      method: "get",
      headers: {
        Authorization: `bearer ${user.accessToken}`,
        "Content-Type": "application/json",
      },
      url: apiURL,
      cancelToken: gatewaysCancel.current.token,
    };

    setIsLoadingGateways(true);
    axios(axiosConfig)
      .then(function (response: { data: GatewayResponse }) {
        setGateways(response.data.data);
        if (isShowSnackbar) {
          dispatch(
            showSnackbar({
              title: "Thành công",
              content: "Tải dữ liệu Gateway thành công!",
              type: "success",
            })
          );
        }
      })
      .catch(function (error) {
        if (axios.isCancel(error)) {
          console.log("Gateways request canceled:", error.message);
          return;
        }
        if (isShowSnackbar) {
          dispatch(
            showSnackbar({
              title: `Tải dữ liệu Gateway lỗi!`,
              content: `${error.message}`,
              type: "error",
            })
          );
        }
      })
      .then(function () {
        setIsLoadingGateways(false);
        gatewaysCancel.current = null;
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (fromDate && toDate) {
      loadMessageStatistics();
      loadPortStatistics();
    }
  }, [fromDate, toDate]);

  // Load gateways on mount and setup polling (every 30 seconds)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadGateways(false); // Initial load

    // Setup polling interval
    pollingInterval.current = setInterval(() => {
      loadGateways(false);
    }, 30000); // 30 seconds

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, []);

  // Cleanup: Cancel pending requests on unmount
  useEffect(() => {
    return () => {
      if (messageStatsCancel.current) {
        messageStatsCancel.current.cancel("Component unmounted");
      }
      if (portStatsCancel.current) {
        portStatsCancel.current.cancel("Component unmounted");
      }
      if (gatewaysCancel.current) {
        gatewaysCancel.current.cancel("Component unmounted");
      }
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, []);

  const handleSearch = () => {
    loadMessageStatistics();
    loadPortStatistics();
  };

  const prepareChartData = () => {
    if (!messageStats?.dailyStatistics) return [];

    return messageStats.dailyStatistics.map((day) => ({
      date: new Date(day.date).toLocaleDateString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
      }),
      total: day.totalMessages,
      sent: day.sent,
      failed: day.failed,
      queued: day.queued,
      sending: day.sending,
    }));
  };

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 90) return "success";
    if (rate >= 70) return "warning";
    return "error";
  };

  const getPortStatus = (
    deviceId?: string,
    portNumber?: number | null
  ): GatewayPort | null => {
    if (!deviceId || portNumber === null || portNumber === undefined)
      return null;

    const gateway = gateways.find((g) => g.id === deviceId);
    if (!gateway) return null;

    return gateway.attributes.ports.find((p) => p.index === portNumber) || null;
  };

  const getShortDeviceName = (
    deviceName?: string,
    port?: number | null
  ): string => {
    if (!deviceName) return "N/A";
    const shortName = deviceName.slice(-4); // Lấy 4 ký tự cuối
    return port !== null && port !== undefined
      ? `${shortName}-P${port}`
      : shortName;
  };

  const handleRetryFailedMessages = () => {
    if (!fromDate || !toDate || isRetrying) return;

    const apiURL = `https://api.hydrabyte.co/msg/util/retry-failed-messages?fromDate=${fromDate}&toDate=${toDate}`;

    setIsRetrying(true);
    axios
      .post(
        apiURL,
        {},
        {
          headers: {
            Authorization: `bearer ${user.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((response) => {
        const { updatedCount } = response.data;
        dispatch(
          showSnackbar({
            title: "Thành công",
            content: `Đã gửi lại thành công: ${updatedCount} tin nhắn lỗi.`,
            type: "success",
          })
        );
        // Reload statistics to reflect changes if necessary
        handleSearch();
      })
      .catch((error) => {
        dispatch(
          showSnackbar({
            title: "Lỗi",
            content: `Gửi lại tin lỗi thất bại: ${error.message}`,
            type: "error",
          })
        );
      })
      .finally(() => {
        setIsRetrying(false);
      });
  };

  const getGateway = (deviceId?: string): Gateway | null => {
    if (!deviceId) return null;
    return gateways.find((g) => g.id === deviceId) || null;
  };

  const handleOpenSmsDialog = (gateway: Gateway) => {
    setSelectedGatewayForSms(gateway);
    setOpenSmsConfirmDialog(true);
  };

  const handleCloseSmsDialog = () => {
    if (isUpdatingSms) return;
    setOpenSmsConfirmDialog(false);
    setSelectedGatewayForSms(null);
  };

  const handleConfirmSmsUpdate = () => {
    if (!selectedGatewayForSms) return;

    const newStatus = !selectedGatewayForSms.attributes.allowOutboundSMS;
    const apiURL = `https://api.hydrabyte.co/msg/gateways/${selectedGatewayForSms.id}/allowOutboundSMS/${newStatus}`;

    setIsUpdatingSms(true);
    axios
      .patch(
        apiURL,
        {},
        {
          headers: {
            Authorization: `bearer ${user.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then(() => {
        dispatch(
          showSnackbar({
            title: "Thành công",
            content: `Đã ${
              newStatus ? "bật" : "tắt"
            } chức năng gửi SMS cho thiết bị ${selectedGatewayForSms.name}`,
            type: "success",
          })
        );
        loadGateways(false); // Refresh data
        handleCloseSmsDialog();
      })
      .catch((error) => {
        dispatch(
          showSnackbar({
            title: "Lỗi",
            content: `Không thể cập nhật trạng thái SMS: ${error.message}`,
            type: "error",
          })
        );
      })
      .finally(() => {
        setIsUpdatingSms(false);
      });
  };

  return (
    <React.Fragment>
      <Breadcrumbs aria-label="breadcrumb">
        <Link color="inherit" to="/">
          Trang Chủ
        </Link>
        <Link color="inherit" to="/lcm">
          LCM
        </Link>
        <Typography color="text.primary">Báo cáo SMS</Typography>
      </Breadcrumbs>

      <Typography variant="h1" className="page-title" color={"primary"}>
        Báo cáo Tin nhắn SMS
      </Typography>
      <Typography variant="subtitle1">
        Thống kê chi tiết về hoạt động gửi tin nhắn SMS
      </Typography>

      {/* Date Range Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Từ ngày"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Đến ngày"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={isProcessing || isLoadingPortStats}
              sx={{ height: "56px" }}
            >
              {isProcessing || isLoadingPortStats ? (
                <CircularProgress size={24} />
              ) : (
                "Tìm kiếm"
              )}
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              color="warning"
              onClick={handleRetryFailedMessages}
              disabled={isRetrying || isProcessing || isLoadingPortStats}
              sx={{ height: "56px" }}
            >
              {isRetrying ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Gửi lại tin lỗi"
              )}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {messageStats && (
        <Box sx={{ marginTop: "10px" }}>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardHeader title="Tổng tin nhắn" />
                <CardContent>
                  <Typography variant="h4" color="primary">
                    {toThousandSeperatorNumber(messageStats.summary.total)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardHeader title="Đã gửi" />
                <CardContent>
                  <Typography variant="h4" color="success.main">
                    {toThousandSeperatorNumber(messageStats.summary.sent)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardHeader title="Thất bại" />
                <CardContent>
                  <Typography variant="h4" color="error.main">
                    {toThousandSeperatorNumber(messageStats.summary.failed)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardHeader title="Tỷ lệ thành công" />
                <CardContent>
                  <Typography variant="h4" color="info.main">
                    {messageStats.summary.successRate.toFixed(2)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Chart */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Biểu đồ sản lượng tin nhắn theo ngày
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={prepareChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <ChartTooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="sent"
                  stroke="#4caf50"
                  name="Đã gửi"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="failed"
                  stroke="#f44336"
                  name="Thất bại"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="queued"
                  stroke="#ff9800"
                  name="Đang chờ"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#2196f3"
                  name="Tổng"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>

          {/* Detailed Statistics Table */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell variant="head" colSpan={8}>
                        <Typography variant="h3" fontSize={"150%"}>
                          Thống kê chi tiết theo ngày
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="head">Ngày</TableCell>
                      <TableCell variant="head" align="right">
                        Tổng tin nhắn
                      </TableCell>
                      <TableCell variant="head" align="right">
                        Đã gửi
                      </TableCell>
                      <TableCell variant="head" align="right">
                        Thất bại
                      </TableCell>
                      <TableCell variant="head" align="right">
                        Đang chờ
                      </TableCell>
                      <TableCell variant="head" align="right">
                        Đang gửi
                      </TableCell>
                      <TableCell variant="head" align="right">
                        Chi phí (VND)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {messageStats.dailyStatistics.map((day: any, i: number) => (
                      <TableRow key={day.date}>
                        <TableCell>
                          {new Date(day.date).toLocaleDateString("vi-VN")}
                        </TableCell>
                        <TableCell align="right">
                          {toThousandSeperatorNumber(day.totalMessages)}
                        </TableCell>
                        <TableCell align="right">
                          {toThousandSeperatorNumber(day.sent)}
                        </TableCell>
                        <TableCell align="right">
                          {toThousandSeperatorNumber(day.failed)}
                        </TableCell>
                        <TableCell align="right">
                          {toThousandSeperatorNumber(day.queued)}
                        </TableCell>
                        <TableCell align="right">
                          {toThousandSeperatorNumber(day.sending)}
                        </TableCell>
                        <TableCell align="right">
                          {day.totalCost > 0
                            ? toThousandSeperatorNumber(day.totalCost)
                            : "0"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Summary Row */}
                    <TableRow
                      sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}
                    >
                      <TableCell>
                        <strong>Tổng cộng</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {toThousandSeperatorNumber(
                            messageStats.summary.total
                          )}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {toThousandSeperatorNumber(messageStats.summary.sent)}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {toThousandSeperatorNumber(
                            messageStats.summary.failed
                          )}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {toThousandSeperatorNumber(
                            messageStats.summary.queued
                          )}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {toThousandSeperatorNumber(
                            messageStats.summary.sending
                          )}
                        </strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {messageStats.summary.totalCost > 0
                            ? toThousandSeperatorNumber(
                                messageStats.summary.totalCost
                              )
                            : "0"}
                        </strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Port Statistics Section */}
      {portStats && (
        <Box sx={{ marginTop: "30px" }}>
          <Typography variant="h2" color="primary" sx={{ mb: 1 }}>
            📡 Báo cáo theo Gateway & Port
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Chip
              label={`${gateways.length} gateways đang hoạt động`}
              color="primary"
              size="small"
            />
            {isLoadingGateways && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Đang cập nhật...
                </Typography>
              </Box>
            )}
            {!isLoadingGateways &&
              gateways.length > 0 &&
              gateways[0]?.attributes?.lastUpdated && (
                <Typography variant="caption" color="text.secondary">
                  Cập nhật lúc:{" "}
                  {new Date(
                    gateways[0].attributes.lastUpdated
                  ).toLocaleTimeString("vi-VN")}
                </Typography>
              )}
          </Box>

          {/* Port Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardHeader title="Tổng Gateway" />
                <CardContent>
                  <Typography variant="h4" color="primary">
                    {toThousandSeperatorNumber(portStats.summary.totalDevices)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardHeader title="Tổng Port" />
                <CardContent>
                  <Typography variant="h4" color="info.main">
                    {toThousandSeperatorNumber(portStats.summary.totalPorts)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardHeader title="Tin nhắn thành công" />
                <CardContent>
                  <Typography variant="h4" color="success.main">
                    {toThousandSeperatorNumber(portStats.summary.totalSuccess)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardHeader title="Tỷ lệ thành công" />
                <CardContent>
                  <Typography variant="h4" color="success.main">
                    {portStats.summary.overallSuccessRate.toFixed(2)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Port Statistics Table */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell variant="head" colSpan={7}>
                        <Typography variant="h3" fontSize={"150%"}>
                          Thống kê chi tiết theo Port
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="head">Gateway-Port</TableCell>
                      <TableCell variant="head" align="center">
                        SIM Info
                      </TableCell>
                      <TableCell variant="head" align="center">
                        Tin nhắn
                      </TableCell>
                      <TableCell variant="head" align="center">
                        Tỷ lệ
                      </TableCell>
                      <TableCell variant="head" align="center">
                        Chức năng
                      </TableCell>
                      <TableCell variant="head" align="center">
                        Trạng thái
                      </TableCell>
                      <TableCell variant="head" align="center">
                        Lần gửi cuối
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {portStats.portStatistics
                      .filter((stat) => stat.port !== null)
                      .map((stat: PortStatistics, i: number) => (
                        <TableRow key={`${stat.deviceId}-${stat.port}-${i}`}>
                          {/* Gateway-Port merged column */}
                          <TableCell>
                            <Chip
                              label={getShortDeviceName(
                                stat.deviceName,
                                stat.port
                              )}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>

                          {/* SIM Info column */}
                          <TableCell align="center">
                            {(() => {
                              const portStatus = getPortStatus(
                                stat.deviceId,
                                stat.port
                              );

                              return (
                                <Box>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    {portStatus?.msisdn || "N/A"}
                                  </Typography>
                                  {portStatus?.operator && (
                                    <Chip
                                      label={portStatus.operator}
                                      size="small"
                                      color="default"
                                      sx={{
                                        mt: 0.5,
                                        fontSize: "0.65rem",
                                        height: "18px",
                                      }}
                                    />
                                  )}
                                </Box>
                              );
                            })()}
                          </TableCell>

                          {/* Messages column with tooltip */}
                          <TableCell align="center">
                            <Tooltip
                              title={
                                <Box>
                                  <Typography variant="caption" display="block">
                                    Tổng:{" "}
                                    {toThousandSeperatorNumber(
                                      stat.totalMessages
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="lightgreen"
                                  >
                                    Thành công:{" "}
                                    {toThousandSeperatorNumber(
                                      stat.successCount
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="lightcoral"
                                  >
                                    Thất bại:{" "}
                                    {toThousandSeperatorNumber(
                                      stat.failedCount
                                    )}
                                  </Typography>
                                </Box>
                              }
                              arrow
                            >
                              <Box sx={{ cursor: "pointer" }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {toThousandSeperatorNumber(
                                    stat.totalMessages
                                  )}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {toThousandSeperatorNumber(stat.successCount)}{" "}
                                  /{" "}
                                  {toThousandSeperatorNumber(stat.failedCount)}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>

                          {/* Success rate column */}
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={stat.successRate}
                                  color={
                                    getSuccessRateColor(stat.successRate) as any
                                  }
                                  sx={{ height: 8, borderRadius: 1 }}
                                />
                              </Box>
                              <Typography
                                variant="body2"
                                color={`${getSuccessRateColor(
                                  stat.successRate
                                )}.main`}
                                sx={{ minWidth: 50, fontWeight: "bold" }}
                              >
                                {stat.successRate.toFixed(1)}%
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Features column */}
                          <TableCell align="center">
                            {(() => {
                              const gateway = getGateway(stat.deviceId);
                              if (!gateway) {
                                return (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    N/A
                                  </Typography>
                                );
                              }

                              const allowSMS =
                                gateway.attributes.allowOutboundSMS ?? false;
                              const allowCall =
                                gateway.attributes.allowOutboundCall ?? false;

                              return (
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    justifyContent: "center",
                                  }}
                                >
                                  <Tooltip
                                    title={
                                      allowSMS
                                        ? "SMS: Cho phép (Nhấn để tắt)"
                                        : "SMS: Không cho phép (Nhấn để bật)"
                                    }
                                  >
                                    <Box
                                      component="span"
                                      onClick={() =>
                                        handleOpenSmsDialog(gateway)
                                      }
                                      sx={{
                                        cursor: "pointer",
                                        display: "inline-flex",
                                        "&:hover": {
                                          opacity: 0.8,
                                        },
                                      }}
                                    >
                                      <SmsIcon
                                        sx={{
                                          color: allowSMS
                                            ? "success.main"
                                            : "error.main",
                                          fontSize: "1.2rem",
                                        }}
                                      />
                                    </Box>
                                  </Tooltip>
                                  <Tooltip
                                    title={
                                      allowCall
                                        ? "Call: Cho phép"
                                        : "Call: Không cho phép"
                                    }
                                  >
                                    <PhoneIcon
                                      sx={{
                                        color: allowCall
                                          ? "success.main"
                                          : "error.main",
                                        fontSize: "1.2rem",
                                      }}
                                    />
                                  </Tooltip>
                                </Box>
                              );
                            })()}
                          </TableCell>

                          {/* Status column */}
                          <TableCell align="center">
                            {(() => {
                              const portStatus = getPortStatus(
                                stat.deviceId,
                                stat.port
                              );
                              if (!portStatus) {
                                return (
                                  <Chip
                                    label="N/A"
                                    size="small"
                                    variant="outlined"
                                  />
                                );
                              }

                              return (
                                <Box>
                                  <Chip
                                    label={
                                      portStatus.isBlocked
                                        ? "Blocked"
                                        : portStatus.state
                                    }
                                    size="small"
                                    color={
                                      portStatus.isBlocked ? "error" : "success"
                                    }
                                    sx={{
                                      mb:
                                        (portStatus.blockReasons?.length ?? 0) >
                                        0
                                          ? 0.5
                                          : 0,
                                    }}
                                  />
                                  {(portStatus.blockReasons?.length ?? 0) >
                                    0 && (
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      color="error.main"
                                      sx={{ fontSize: "0.7rem" }}
                                    >
                                      {portStatus.blockReasons!.join(", ")}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })()}
                          </TableCell>
                          <TableCell align="center">
                            {stat.lastSentAt ? (
                              <>
                                <Typography variant="body2">
                                  {new Date(stat.lastSentAt).toLocaleDateString(
                                    "vi-VN"
                                  )}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {new Date(stat.lastSentAt).toLocaleTimeString(
                                    "vi-VN"
                                  )}
                                </Typography>
                              </>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Box>
      )}
      {/* SMS Confirm Dialog */}
      <Dialog open={openSmsConfirmDialog} onClose={handleCloseSmsDialog}>
        <DialogTitle>Xác nhận thay đổi cấu hình SMS</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bạn có chắc chắn muốn{" "}
            <strong>
              {selectedGatewayForSms?.attributes.allowOutboundSMS
                ? "TẮT"
                : "BẬT"}
            </strong>{" "}
            chức năng gửi SMS cho thiết bị{" "}
            <strong>{selectedGatewayForSms?.name}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseSmsDialog}
            color="inherit"
            disabled={isUpdatingSms}
          >
            Hủy
          </Button>
          <Button
            onClick={handleConfirmSmsUpdate}
            color="primary"
            variant="contained"
            autoFocus
            disabled={isUpdatingSms}
          >
            {isUpdatingSms ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Đồng ý"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
